/**
 * The single sessionStorage key holding the current customer's customization flags.
 *
 * The flags come from `POST /transaction/customization-list`, which returns a
 * `List<string>`, and they gate per-customer features across the dashboards, the
 * workspace edit screens and the reports. They are NOT identity claims: identity is read
 * from the signed JWT through `UserService` / `SessionClaimsService`, never from here.
 */
export const CUSTOMIZATION_FLAGS_KEY = 'customizationList';

/**
 * The retired snake_case spelling of {@link CUSTOMIZATION_FLAGS_KEY}.
 *
 * Never written any more. It survives here only so {@link migrateLegacyCustomizationFlags}
 * can pick up a session that already holds it and delete it, which is what keeps a tab
 * that was open across the deploy working: sessionStorage is tab-local, so such a tab
 * still carries a value written by the previous build.
 */
const LEGACY_CUSTOMIZATION_FLAGS_KEY = 'customisation_list';

/**
 * Reads a sessionStorage entry without ever throwing.
 *
 * `sessionStorage` access itself can throw when storage is blocked (private-mode
 * variants, hardened enterprise policies), and this module is called during bootstrap on
 * paths that must not fail.
 * @param key - The storage key.
 * @returns The stored string, or null when absent or unreadable.
 */
function readRaw(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * Turns whatever is stored, or whatever the API returned, into a clean flag list.
 *
 * Accepts every shape this value has historically taken, because all of them are still
 * in circulation:
 * - a `string[]`, which is what the API returns;
 * - a JSON array string, written by the dashboards;
 * - a comma-joined string, produced whenever an array was handed straight to `setItem`
 *   (`String(['A','B'])` is `'A,B'`) — the shape a pre-upgrade tab still holds;
 * - anything else, including the literal `"undefined"` a failed response used to store.
 *
 * Empty and whitespace-only entries are dropped, so a flag-less customer yields `[]` and
 * never `['']`. That distinction matters: `''.split(',')` returns `['']`, which looks like
 * one flag to server-side `[MinLength(1)]` validation and like a truthy flag to callers.
 * @param value - The raw stored string, the API result, or anything else.
 * @returns The flags, or an empty array when none can be read.
 */
function normalizeFlags(value: unknown): string[] {
  if (value === null || value === undefined) {
    return [];
  }

  if (Array.isArray(value)) {
    return value
      .filter((entry) => entry !== null && entry !== undefined)
      .map((entry) => String(entry).trim())
      .filter((entry) => entry.length > 0);
  }

  if (typeof value !== 'string') {
    return [];
  }

  const text = value.trim();
  if (!text || text === 'undefined' || text === 'null') {
    return [];
  }

  // A value that opens a JSON array is treated as JSON and nothing else. If it fails to
  // parse it is corrupt, and comma-splitting it would manufacture a bogus flag such as
  // `["unclosed` that goes on to travel in `customization_list` API payloads. An empty
  // list is the honest reading of a corrupt value.
  if (text.startsWith('[')) {
    try {
      const parsed: unknown = JSON.parse(text);
      return Array.isArray(parsed) ? normalizeFlags(parsed) : [];
    } catch {
      return [];
    }
  }

  return text
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

/**
 * The current customer's customization flags.
 *
 * The one way to read them. Replaces the defensive parse that was hand-copied into
 * `base-grid.component.ts`, `layout.component.ts`, `reginsights-access.service.ts`,
 * `audit-trail-timeline.ts` and `compliance-calender.component.ts`, each of which
 * re-derived the same "try JSON, else split on commas" logic.
 * @returns The flags, or an empty array when none are stored or the value is unusable.
 * Never throws.
 */
export function readCustomizationFlags(): string[] {
  const current = readRaw(CUSTOMIZATION_FLAGS_KEY);
  const flags = normalizeFlags(current);
  if (flags.length) {
    return flags;
  }
  return normalizeFlags(readRaw(LEGACY_CUSTOMIZATION_FLAGS_KEY));
}

/**
 * Whether the customer holds a given flag.
 *
 * An EXACT, case-insensitive comparison, deliberately not the substring test that several
 * call sites used against the raw stored string. Substring matching is wrong here because
 * the flag vocabulary contains prefixes of itself: `MGMT_Total` is a prefix of
 * `MGMT_Total_Unique`, `NotApplicablehide` of `NotApplicablehideRR` and
 * `NotApplicablehideChecklist`, and `NotComplied` of `NotCompliedCustID`. A substring test
 * for the shorter flag fires for a customer who holds only the longer one.
 * @param flag - The flag name to look for.
 * @returns True when the customer holds exactly that flag.
 */
export function hasCustomizationFlag(flag: string): boolean {
  const wanted = flag?.trim().toLowerCase();
  if (!wanted) {
    return false;
  }
  return readCustomizationFlags().some((entry) => entry.toLowerCase() === wanted);
}

/**
 * Stores the customization flags returned by the customization-list endpoint.
 *
 * The one way to write them, so the stored shape cannot diverge between entry points
 * again. Previously login and the company switch handed the raw array to `setItem`, which
 * coerced it to a comma-joined string, while the dashboards stored JSON — so a reader that
 * parsed JSON threw after a company switch. Everything is stored as a JSON array here.
 *
 * Only {@link CUSTOMIZATION_FLAGS_KEY} is written. The retired spelling is not kept in
 * step, because nothing writes or keeps it any more — see
 * {@link migrateLegacyCustomizationFlags}.
 * @param result - The API result, normally a `string[]`. Any other shape is normalised,
 * and an unusable one stores an empty list rather than throwing.
 * @returns Nothing.
 */
export function writeCustomizationFlags(result: unknown): void {
  const serialized = JSON.stringify(normalizeFlags(result));
  try {
    // The retired copy goes first. Any value under that spelling would now be a second,
    // diverging source of truth, which is the defect this whole change exists to remove;
    // and removing it before the write means a setItem that throws (storage full) cannot
    // leave a stale copy behind for the read fallback to pick up. removeItem itself does
    // not throw on quota, so the ordering costs nothing on the happy path.
    sessionStorage.removeItem(LEGACY_CUSTOMIZATION_FLAGS_KEY);
    sessionStorage.setItem(CUSTOMIZATION_FLAGS_KEY, serialized);
  } catch {
    // Storage unavailable or full; the app falls back to "no flags" rather than failing
    // the sign-in or company switch that triggered this write.
  }
}

/**
 * Moves a value left under the retired spelling onto the current key, then deletes it.
 *
 * Call once when the app starts. It exists for one situation: a tab that was already open
 * when this build deployed. sessionStorage is per tab and survives a reload, so that tab
 * still holds the flags under the old spelling, written by the previous build, and quite
 * possibly in the old comma-joined shape. Without this, the screens that read the current
 * key would see nothing and silently switch their features off until the user signs in
 * again.
 *
 * Deliberately non-destructive when the current key is already present, even as an empty
 * list: that value was written by a newer build and is the truth, so the retired one is
 * simply dropped.
 *
 * Safe to delete this function, its call site and {@link LEGACY_CUSTOMIZATION_FLAGS_KEY}
 * once no session started on a build older than this one can still be open — a release or
 * two, given sessions are tab-local.
 * @returns Nothing. Never throws.
 */
export function migrateLegacyCustomizationFlags(): void {
  const legacy = readRaw(LEGACY_CUSTOMIZATION_FLAGS_KEY);
  if (legacy === null) {
    return;
  }
  try {
    // Presence, not emptiness, decides. A current key holding `[]` is a real answer (a
    // customer with no flags) written by a newer build, and must not be overridden by
    // whatever an older build left under the retired spelling.
    if (readRaw(CUSTOMIZATION_FLAGS_KEY) === null) {
      sessionStorage.setItem(CUSTOMIZATION_FLAGS_KEY, JSON.stringify(normalizeFlags(legacy)));
    }
    sessionStorage.removeItem(LEGACY_CUSTOMIZATION_FLAGS_KEY);
  } catch {
    // Storage unavailable; the reader's own fallback still covers this session.
  }
}

/**
 * Removes the stored customization flags.
 *
 * Called when the session's customer changes, so the previous customer's flags cannot
 * gate the next one's screens.
 * @returns Nothing.
 */
export function clearCustomizationFlags(): void {
  try {
    sessionStorage.removeItem(CUSTOMIZATION_FLAGS_KEY);
    sessionStorage.removeItem(LEGACY_CUSTOMIZATION_FLAGS_KEY);
  } catch {
    // Nothing to do — an unreadable store is already effectively cleared.
  }
}
